import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { SlidersHorizontal, ArrowUpDown, ChevronDown } from "lucide-react";
import ProductCard from "../components/ProductCard";
import FilterPanel from "../components/FilterPanel";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { filterOptions, sortOptions } from "../mock";
import { getProducts } from "../api";
import { useSeo } from "../hooks/useSeo";

const emptyFilters = () => ({
  soorten: [], andere: [], leeftijden: [], formaten: [], personen: [], themas: [],
  locaties: [], evenementen: [], price: [filterOptions.priceMin, filterOptions.priceMax],
});

const personMatch = (bucket, n) => {
  if (bucket === "Tot 6") return n <= 6;
  if (bucket === "6 - 10") return n >= 6 && n <= 10;
  if (bucket === "10+") return n >= 10;
  return true;
};

const Products = () => {
  const [params] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(emptyFilters());
  const [sort, setSort] = useState("populair");
  const [showFilters, setShowFilters] = useState(false);

  useSeo({
    title: "Springkussens huren | Aanbod en prijzen | Jump en Joy",
    description: "Bekijk ons complete aanbod springkussens, glijbanen en stormbanen. Controleer de beschikbaarheid en reserveer direct online. Levering en opbouw inbegrepen.",
    keywords: "springkussen huren, springkasteel aanbod, glijbaan huren, stormbaan huren, prijzen springkussen, attractie verhuur",
  });

  useEffect(() => {
    getProducts().then((d) => { setProducts(d); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    const cat = params.get("cat");
    if (cat && filterOptions.soorten.includes(cat)) {
      setFilters((f) => ({ ...f, soorten: [cat] }));
      setShowFilters(false);
    } else if (cat && (filterOptions.andere || []).includes(cat)) {
      setFilters((f) => ({ ...f, andere: [cat] }));
      setShowFilters(false);
    }
  }, [params]);

  const overlaps = (arr, sel) => sel.length === 0 || (arr || []).some((x) => sel.includes(x));

  const activeCount = useMemo(() => {
    const f = filters;
    let n = f.soorten.length + f.andere.length + f.leeftijden.length + f.formaten.length + f.personen.length + f.themas.length + f.locaties.length + f.evenementen.length;
    if (f.price[0] !== filterOptions.priceMin || f.price[1] !== filterOptions.priceMax) n += 1;
    return n;
  }, [filters]);

  const result = useMemo(() => {
    let list = products.filter((p) => {
      const selectedCats = [...filters.soorten, ...filters.andere];
      if (selectedCats.length && !selectedCats.includes(p.category)) return false;
      if (filters.leeftijden.length && !filters.leeftijden.includes(p.leeftijd)) return false;
      if (filters.formaten.length && !filters.formaten.includes(p.formaat)) return false;
      if (filters.personen.length && !filters.personen.some((b) => personMatch(b, p.personen))) return false;
      if (!overlaps(p.themas, filters.themas)) return false;
      if (!overlaps(p.locaties, filters.locaties)) return false;
      if (!overlaps(p.evenementen, filters.evenementen)) return false;
      if (p.price < filters.price[0] || p.price > filters.price[1]) return false;
      return true;
    });
    const by = {
      "populair": (a, b) => (b.popularity || 0) - (a.popularity || 0),
      "price-asc": (a, b) => a.price - b.price,
      "price-desc": (a, b) => b.price - a.price,
      "nieuwst": (a, b) => (b.order || 0) - (a.order || 0),
      "grootte": (a, b) => (b.area || 0) - (a.area || 0),
    };
    return [...list].sort(by[sort]);
  }, [products, filters, sort]);

  const reset = () => setFilters(emptyFilters());

  return (
    <div className="wrap py-12">
      <div className="text-center max-w-2xl mx-auto">
        <span className="inline-block bg-brand-blue text-white text-xs font-bold rounded-full px-4 py-1.5">Ons aanbod</span>
        <h1 className="font-display text-brand-navy text-4xl sm:text-5xl mt-4">Onze springkussens</h1>
        <p className="text-slate-500 mt-4">
          Filter en sorteer op soort, leeftijd, formaat, thema, prijs en meer. Geleverd, opgebouwd en weer opgehaald.
        </p>
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3 mt-10">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowFilters((v) => !v)}
            className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold transition-colors ${showFilters ? "bg-brand-blue text-white" : "bg-white border border-slate-200 text-brand-navy hover:border-brand-blue"}`}
          >
            <SlidersHorizontal className="w-4 h-4" /> Filters
            {activeCount > 0 && <span className="ml-1 inline-flex items-center justify-center min-w-5 h-5 px-1 rounded-full bg-brand-pink text-white text-xs">{activeCount}</span>}
            <ChevronDown className={`w-4 h-4 transition-transform ${showFilters ? "rotate-180" : ""}`} />
          </button>
          <p className="text-sm text-slate-500"><span className="font-bold text-brand-navy">{result.length}</span> resultaten</p>
        </div>
        <div className="flex items-center gap-2">
          <ArrowUpDown className="w-4 h-4 text-slate-400 hidden sm:block" />
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="w-[200px] rounded-full"><SelectValue /></SelectTrigger>
            <SelectContent>
              {sortOptions.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Expandable filter panel */}
      {showFilters && (
        <div className="mega-anim rounded-3xl border border-slate-100 shadow-sm p-5 sm:p-7 mt-4">
          <FilterPanel filters={filters} setFilters={setFilters} options={filterOptions} onReset={reset} />
        </div>
      )}

      {/* Results */}
      <div className="mt-8">
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => <div key={i} className="h-80 rounded-2xl bg-slate-100 animate-pulse" />)}
          </div>
        ) : result.length === 0 ? (
          <div className="rounded-3xl border border-slate-100 p-12 text-center">
            <p className="font-display text-xl text-brand-navy">Geen springkussens gevonden</p>
            <p className="text-slate-500 mt-2">Pas je filters aan om meer resultaten te zien.</p>
            <button onClick={reset} className="inline-flex mt-5 rounded-full bg-brand-blue hover:bg-brand-bluedark text-white font-bold px-6 py-3 transition-colors">Filters wissen</button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {result.map((p) => <ProductCard key={p.slug} product={p} showSpecs />)}
          </div>
        )}
      </div>

      <div className="rounded-[2rem] bg-brand-blue text-white p-8 sm:p-12 mt-16 text-center">
        <h2 className="font-display text-2xl sm:text-3xl">Niet gevonden wat je zoekt?</h2>
        <p className="text-blue-50 mt-3 max-w-xl mx-auto">
          We hebben nog meer in ons aanbod en denken graag mee over de beste keuze voor jouw locatie en gelegenheid.
        </p>
        <Link to="/contact" className="inline-flex mt-6 rounded-full bg-white text-brand-blue font-bold px-7 py-3.5 hover:bg-slate-100 transition-colors">
          Neem contact op
        </Link>
      </div>
    </div>
  );
};

export default Products;
